import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '@/hooks/use-app-context';
import { TaskHeader, TaskSessionComplete, TaskShell, TaskStepDots, kitLang, taskSteps } from '@/features/contribution-kit';
import {
  ValidateTranscriptionActionBar,
  ValidateTranscriptionDecisionPanel,
  ValidateTranscriptionItemCard,
  ValidateTranscriptionSetup,
  outcomeCounts,
  stepOutcome,
  useValidateTranscriptionSession,
  validateTranscriptionCopy,
} from '@/features/validate-transcription';
import { ValidationGateProgressStrip } from '@/features/validation-gate';

/** "Valider des textes": listen to an audio and validate, correct or reject its proposed text. */
export default function ValidateTranscription() {
  const { t, user, language } = useApp();
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const currentLang = lang || language || 'fr';
  const copy = validateTranscriptionCopy[kitLang(currentLang)];
  const session = useValidateTranscriptionSession();
  const s = (key: string) => String((t as Record<string, unknown>)[key] ?? '');

  const goHome = () => navigate(`/${currentLang}`);

  if (!session.sessionStarted) {
    return (
      <ValidateTranscriptionSetup
        t={t}
        copy={copy}
        languages={session.languages}
        selectedLanguage={session.selectedLanguage}
        onLanguageChange={session.setSelectedLanguage}
        source={session.source}
        onSourceChange={session.setSource}
        canStart={Boolean(session.selectedLanguage && user?.id)}
        isLoading={session.isLoading}
        onStart={session.startLinkedSession}
        onBack={goHome}
      />
    );
  }

  if (session.sessionComplete) {
    const counts = outcomeCounts(session.items);
    return (
      <TaskSessionComplete
        title={copy.doneTitle}
        description={copy.doneDescription}
        stats={[
          { label: copy.doneValidated, value: counts.validated, tone: 'success' },
          { label: copy.doneRejected, value: counts.rejected, tone: counts.rejected > 0 ? 'danger' : 'default' },
          { label: copy.doneFinalized, value: counts.finalized },
        ]}
        continueLabel={copy.doneContinue}
        homeLabel={s('back')}
        onContinue={session.restartSession}
        onHome={goHome}
      />
    );
  }

  const { current } = session;
  const steps = taskSteps(session.itemsLength, session.currentIndex, (index) => stepOutcome(session.items[index]));

  return (
    <TaskShell
      width="wide"
      header={
        <TaskHeader
          task="verifyText"
          index={session.currentIndex}
          total={session.itemsLength}
          positionLabel={s('audioXofY')
            .replace('{current}', String(Math.min(session.currentIndex + 1, session.itemsLength)))
            .replace('{total}', String(session.itemsLength))}
          progress={session.progressPercent}
          itemId={current?.transcriptionId ?? current?.id}
          onBack={goHome}
          meta={
            <>
              <span>{session.selectedLanguage}</span>
              <span className="tabular-nums">
                {session.validatedCount}/{session.itemsLength} {s('validatedSuffix')}
              </span>
            </>
          }
        />
      }
      notices={<ValidationGateProgressStrip />}
      actionBar={
        current ? (
        <ValidateTranscriptionActionBar
          t={t}
          copy={copy}
          isFirst={session.currentIndex === 0}
          text={session.text}
          rejectionReason={session.rejectionReason}
          isLoading={session.isLoading}
          isSubmitting={session.isSubmitting}
          hasItem={current !== null}
          onPrevious={session.previous}
          onReject={session.rejectText}
          onValidate={session.validateText}
          onNext={session.goNext}
          onTogglePlay={session.playAudio}
        />
        ) : null
      }
    >
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-start">
        {current ? (
          <ValidateTranscriptionItemCard
            t={t}
            current={current}
            isPlaying={session.isPlaying}
            audioRef={session.audioRef}
            canvasRef={session.canvasRef}
            onPlayAudio={session.playAudio}
            onAudioEnded={session.onAudioEnded}
            footer={
              <TaskStepDots
                steps={steps}
                labels={{ current: copy.stepCurrent, done: copy.stepDone, rejected: copy.stepRejected, todo: copy.stepTodo }}
              />
            }
          />
        ) : (
          <div />
        )}

        <ValidateTranscriptionDecisionPanel
          t={t}
          copy={copy}
          text={session.text}
          onTextChange={session.setText}
          onFinalize={session.finalizeText}
          rejectionReason={session.rejectionReason}
          onRejectionReasonChange={session.setRejectionReason}
          detailedRejectionReason={session.detailedRejectionReason}
          onDetailedRejectionReasonChange={session.setDetailedRejectionReason}
          isLoading={session.isLoading}
          isSubmitting={session.isSubmitting}
        />
      </div>
    </TaskShell>
  );
}
